import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Volume2, VolumeX } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

interface SignersBubblesProps {
  messages: Tables<"messages">[];
  contributions?: Tables<"contributions">[];
  maxVisible?: number;
}

export function getInitials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const COLORS = [
  "bg-rose-100 text-rose-700",
  "bg-amber-100 text-amber-700",
  "bg-emerald-100 text-emerald-700",
  "bg-sky-100 text-sky-700",
  "bg-violet-100 text-violet-700",
  "bg-fuchsia-100 text-fuchsia-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
];

export function colorForName(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return COLORS[Math.abs(hash) % COLORS.length];
}

interface BubbleMediaProps {
  message: Tables<"messages">;
  size: "sm" | "lg";
}

export function BubbleMedia({ message, size }: BubbleMediaProps) {
  const dimensions = size === "lg" ? "w-20 h-20" : "w-14 h-14";
  const textSize = size === "lg" ? "text-xl" : "text-xs";
  const ringClass = "ring-background";

  if (message.video_url) {
    return (
      <div className={`${dimensions} rounded-full overflow-hidden ring-2 ${ringClass} relative group`}>
        <video src={message.video_url} autoPlay loop muted playsInline className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
          <Play size={size === "lg" ? 20 : 14} className="text-white fill-white" />
        </div>
      </div>
    );
  }

  if (message.gif_url) {
    return (
      <div className={`${dimensions} rounded-full overflow-hidden ring-2 ${ringClass}`}>
        <img src={message.gif_url} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }

  if (message.photo_url) {
    return (
      <div className={`${dimensions} rounded-full overflow-hidden ring-2 ${ringClass}`}>
        <img src={message.photo_url} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }

  if (message.audio_url) {
    return (
      <div className={`${dimensions} rounded-full ring-2 ${ringClass} flex items-center justify-center ${colorForName(message.contributor_name)}`}>
        <Volume2 size={size === "lg" ? 22 : 16} className="opacity-70" />
      </div>
    );
  }

  return (
    <div className={`${dimensions} rounded-full ring-2 ${ringClass} flex items-center justify-center font-medium ${textSize} ${colorForName(message.contributor_name)}`}>
      {getInitials(message.contributor_name)}
    </div>
  );
}

// Grouped person type
interface GroupedPerson {
  key: string;
  name: string;
  email: string | null;
  messages: Tables<"messages">[];
  totalContributed: number;
  hasContribution: boolean;
}

function groupByPerson(
  messages: Tables<"messages">[],
  contributions: Tables<"contributions">[]
): GroupedPerson[] {
  const map = new Map<string, GroupedPerson>();

  for (const msg of messages) {
    const key = msg.contributor_email || msg.contributor_name;
    const existing = map.get(key);
    if (existing) {
      existing.messages.push(msg);
    } else {
      map.set(key, {
        key,
        name: msg.contributor_name,
        email: msg.contributor_email,
        messages: [msg],
        totalContributed: 0,
        hasContribution: false,
      });
    }
  }

  // Attach contributions
  for (const c of contributions) {
    if (c.status !== "completed") continue;
    const key = c.contributor_email || c.contributor_name;
    const person = map.get(key);
    if (person) {
      person.hasContribution = true;
      person.totalContributed += Number(c.amount);
    }
  }

  return Array.from(map.values());
}

// Detail view showing all messages from one person
function GroupedSignerDetail({
  person,
  onClose,
}: {
  person: GroupedPerson;
  onClose: () => void;
}) {
  const [muted, setMuted] = useState(true);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-background rounded-3xl p-6 w-full max-w-sm shadow-2xl relative max-h-[80vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors z-10"
        >
          <X size={16} />
        </button>

        <div className="flex flex-col items-center text-center space-y-4">
          <BubbleMedia message={person.messages[0]} size="lg" />

          <div>
            <h3 className="text-lg font-semibold text-foreground">{person.name}</h3>
            {person.hasContribution && (
              <span className="inline-block mt-1 text-xs font-medium bg-emerald-100 text-emerald-700 px-2.5 py-0.5 rounded-full">
                Chipped in ${person.totalContributed.toFixed(0)}
              </span>
            )}
            {person.messages.length > 1 && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {person.messages.length} wishes
              </p>
            )}
          </div>

          {/* All messages from this person */}
          <div className="w-full space-y-4">
            {person.messages.map((msg, idx) => (
              <div key={msg.id} className="w-full">
                {person.messages.length > 1 && idx > 0 && (
                  <div className="h-px bg-border my-2" />
                )}

                {msg.message_text && (
                  <p className="text-sm text-muted-foreground leading-relaxed italic">
                    "{msg.message_text}"
                  </p>
                )}

                {msg.video_url && (
                  <div className="w-full rounded-2xl overflow-hidden relative bg-black mt-2">
                    <video
                      src={msg.video_url}
                      autoPlay
                      loop
                      muted={muted}
                      playsInline
                      className="w-full max-h-64 object-contain"
                    />
                    <button
                      onClick={() => setMuted(!muted)}
                      className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center"
                    >
                      {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                    </button>
                  </div>
                )}

                {msg.gif_url && !msg.video_url && (
                  <img src={msg.gif_url} alt="GIF" className="w-full rounded-2xl mt-2" />
                )}

                {msg.photo_url && !msg.video_url && !msg.gif_url && (
                  <img src={msg.photo_url} alt="Photo" className="w-full rounded-2xl object-cover max-h-64 mt-2" />
                )}

                {msg.audio_url && (
                  <audio src={msg.audio_url} controls className="w-full mt-2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Legacy export for backwards compatibility
export function SignerDetail({
  message,
  contribution,
  onClose,
}: {
  message: Tables<"messages">;
  contribution?: Tables<"contributions">;
  onClose: () => void;
}) {
  const person: GroupedPerson = {
    key: message.contributor_email || message.contributor_name,
    name: message.contributor_name,
    email: message.contributor_email,
    messages: [message],
    totalContributed: contribution ? Number(contribution.amount) : 0,
    hasContribution: !!contribution && contribution.status === "completed",
  };
  return <GroupedSignerDetail person={person} onClose={onClose} />;
}

export default function SignersBubbles({ messages, contributions = [], maxVisible = 12 }: SignersBubblesProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const grouped = useMemo(
    () => groupByPerson(messages, contributions),
    [messages, contributions]
  );

  const visible = grouped.slice(0, maxVisible);
  const overflow = grouped.length - maxVisible;

  const selectedPerson = grouped.find((p) => p.key === selectedKey);

  if (messages.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-muted-foreground">Be the first to sign!</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="grid grid-cols-5 gap-x-1 gap-y-2 justify-items-center px-1">
          {visible.map((person, i) => (
            <motion.button
              key={person.key}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.04, type: "spring", stiffness: 300, damping: 20 }}
              onClick={() => setSelectedKey(person.key)}
              className="flex flex-col items-center gap-1.5 group cursor-pointer"
              title={person.name}
            >
              <div className="relative transition-transform group-hover:scale-110 overflow-visible">
                <BubbleMedia message={person.messages[0]} size="sm" />
                {person.hasContribution && (
                  <span className="absolute -bottom-0.5 -right-0.5 min-w-5 h-5 rounded-full bg-amber-400 text-amber-900 flex items-center justify-center text-[9px] font-bold ring-2 ring-background shadow-sm px-1">
                    ${person.totalContributed.toFixed(0)}
                  </span>
                )}
              </div>
              <div className="flex flex-col items-center">
                <span className="text-[10px] text-muted-foreground font-medium max-w-[56px] truncate leading-tight">
                  {person.name.split(" ")[0]}
                </span>
                {person.messages.length > 1 && (
                  <span className="text-[9px] text-muted-foreground/60">
                    ×{person.messages.length}
                  </span>
                )}
              </div>
            </motion.button>
          ))}

          {overflow > 0 && (
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-14 h-14 rounded-full ring-2 ring-background flex items-center justify-center bg-muted text-muted-foreground text-xs font-medium">
                +{overflow}
              </div>
              <span className="text-[10px] text-muted-foreground font-medium">more</span>
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {selectedPerson && (
          <GroupedSignerDetail
            person={selectedPerson}
            onClose={() => setSelectedKey(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
